/* React wrapper around the faithful Watch3D renderer.
 *
 * Owns a <canvas> and drives Watch3D.mountLive / updateLive / unmountLive from
 * the product spec, exactly like the prototype's Design Studio (mountLive on
 * render, updateLive when the spec changes, unmountLive on unmount). three.js is
 * loaded from a CDN inside watch3d.ts, so no extra install is needed. */
import { useEffect, useRef } from 'react';
import { Watch3D } from './watch3d';
import { useTheme } from '../hooks/useTheme';

/** A full product spec (engine COMPONENTS ids) — drives the rendered watch. */
export interface WatchSpec {
  casing?: string; strap?: string; size?: string; style?: string;
  display?: string; color?: string; sensors?: string[];
}

export default function WatchViewer({ spec, brand }: { spec: WatchSpec; brand?: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // The studio environment is prefiltered from a room tinted by the theme, so a
  // theme switch has to rebuild the scene. It is a rare, deliberate action.
  const theme = useTheme();

  // Mount once per theme; the renderer owns its own RAF loop + drag handlers.
  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    Watch3D.mountLive(cv, Watch3D.prodCfg(spec, brand));
    return () => Watch3D.unmountLive();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme]);

  // Rebuild the watch whenever any spec field changes (cheap — geometry only).
  useEffect(() => {
    Watch3D.updateLive(Watch3D.prodCfg(spec, brand));
  }, [spec.casing, spec.strap, spec.size, spec.style, spec.display, spec.color, (spec.sensors ?? []).join('+'), brand]);

  return <canvas ref={canvasRef} className="block h-full w-full" />;
}
